import { Controller } from "@hotwired/stimulus"

// Reusable multi-select picker with text search and chip-based removal.
//
// Each option carries its own `data-field-name`, so a single picker can
// manage multiple arrays (e.g. `convo[contact_ids][]` and
// `convo[user_ids][]`) inside one form. Hidden inputs are created/removed
// as options are toggled.
//
// Usage:
//   <div data-controller="attachable-picker">
//     <div data-attachable-picker-target="chips"></div>
//     <input data-attachable-picker-target="search"
//            data-action="input->attachable-picker#filter">
//     <div data-attachable-picker-target="list">
//       <button type="button"
//               data-attachable-picker-target="option"
//               data-action="click->attachable-picker#toggle"
//               data-value="1"
//               data-label="Jane Doe"
//               data-field-name="convo[contact_ids][]"
//               data-search="jane doe jane@example.com"
//               data-selected="false">
//         …markup…
//       </button>
//     </div>
//     <div data-attachable-picker-target="empty" hidden>No matches</div>
//     <template data-attachable-picker-target="chipTemplate">
//       <span class="attachable-picker-chip" data-chip-key="">
//         <span class="attachable-picker-chip-label"></span>
//         <button type="button" data-action="click->attachable-picker#remove">
//           <i class="bi bi-x"></i>
//         </button>
//       </span>
//     </template>
//   </div>
export default class extends Controller {
  static targets = ["search", "option", "chips", "chipTemplate", "empty", "list"]

  connect() {
    this.optionTargets.forEach(option => {
      if (option.dataset.selected === "true") {
        this.#selectOption(option, { skipHiddenInput: true })
      }
    })
    this.#applyFilter()
  }

  toggle(event) {
    event.preventDefault()
    const option = event.currentTarget
    if (option.dataset.selected === "true") return

    this.#selectOption(option)
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }
    this.#applyFilter()
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  remove(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest("[data-chip-key]")
    if (!chip) return

    const key = chip.dataset.chipKey
    const option = this.#findOptionByKey(key)
    if (option) {
      option.dataset.selected = "false"
      option.classList.remove("is-selected")
      option.removeAttribute("aria-pressed")
    }

    const hiddenInput = this.element.querySelector(`input[type="hidden"][data-chip-key="${CSS.escape(key)}"]`)
    if (hiddenInput) hiddenInput.remove()

    chip.remove()
    this.#applyFilter()
  }

  filter() {
    this.#applyFilter()
  }

  #selectOption(option, { skipHiddenInput = false } = {}) {
    const key = this.#optionKey(option)
    option.dataset.selected = "true"
    option.classList.add("is-selected")
    option.setAttribute("aria-pressed", "true")

    this.#appendChip(option, key)
    if (!skipHiddenInput) {
      this.#appendHiddenInput(option, key)
    } else {
      const existing = this.element.querySelector(`input[type="hidden"][data-chip-key="${CSS.escape(key)}"]`)
      if (existing) existing.dataset.chipKey = key
    }
  }

  #appendChip(option, key) {
    if (!this.hasChipsTarget || !this.hasChipTemplateTarget) return
    const existing = this.chipsTarget.querySelector(`[data-chip-key="${CSS.escape(key)}"]`)
    if (existing) return

    const fragment = this.chipTemplateTarget.content.cloneNode(true)
    const chip = fragment.querySelector("[data-chip-key]")
    if (!chip) return

    chip.dataset.chipKey = key
    const label = chip.querySelector("[data-chip-label]") || chip.querySelector(".attachable-picker-chip-label")
    if (label) label.textContent = option.dataset.label || ""

    const type = option.dataset.type
    if (type) chip.dataset.type = type

    this.chipsTarget.appendChild(chip)
  }

  #appendHiddenInput(option, key) {
    const existing = this.element.querySelector(`input[type="hidden"][data-chip-key="${CSS.escape(key)}"]`)
    if (existing) return

    const fieldName = option.dataset.fieldName
    if (!fieldName) return

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = fieldName
    input.value = option.dataset.value || ""
    input.dataset.chipKey = key
    this.element.appendChild(input)
  }

  #findOptionByKey(key) {
    return this.optionTargets.find(option => this.#optionKey(option) === key)
  }

  #optionKey(option) {
    const type = option.dataset.type || "item"
    const value = option.dataset.value || ""
    return `${type}:${value}`
  }

  #applyFilter() {
    const query = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    let visible = 0

    this.optionTargets.forEach(option => {
      if (option.dataset.selected === "true") {
        option.hidden = true
        return
      }

      const haystack = (option.dataset.search || option.textContent || "").toLowerCase()
      const matches = !query || haystack.includes(query)
      option.hidden = !matches
      if (matches) visible++
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visible !== 0
    }
  }
}
